import {UserCache} from "./types";
import {JsonObject} from "./json/json-object";
import {JsonProperty} from "./json/json-property";
import {ProvisioningContext, Rel, SystemProvisioningIds} from "./database";
import {Auth, getAuth} from "@firebase/auth";
import {JSON_OBJECT} from "./json/helpers";
import {remove} from "@firebase/database";
import {md5_uuid} from "./md5";
import {BaseApp, getProvisioningId} from "./BaseApp";
import {FormGen} from "./formgen";
import {StringUtil} from "./string_util";
import {passkey} from "./passkey";

@JsonObject()
export class IdType {

  constructor(@JsonProperty() readonly id: string, @JsonProperty() readonly type: string) {
  }
}

export enum EntityType {
  USER = "user",
}

@JsonObject()
export abstract class Entity {

  @JsonProperty()
  firstname: string;
  @JsonProperty()
  lastname: string;
  @JsonProperty()
  profilePhoto: string;

  protected constructor(firstname?: string, lastname?: string, profilePhoto?: string) {
    this.firstname = firstname;
    this.lastname = lastname;
    this.profilePhoto = profilePhoto;
  }
}

@JsonObject()
export abstract class BaseUser extends Entity {

  @JsonProperty()
  uid: string;

  protected constructor(uid: string, firstname: string, lastname: string, profilePhoto: string) {
    super(firstname, lastname, profilePhoto);
    this.uid = uid;
  }
}

@JsonObject()
export class User extends BaseUser {

  @JsonProperty()
  readonly extras: any = {}; // App-specific data should be a field in this object, e.g. extras["isTestUser"] = true.

  readonly isGuest: boolean;

  constructor(uid: string, firstname: string, lastname: string, profilePhoto?: string,
              @JsonProperty() readonly email?: string,
              @JsonProperty() readonly created?: number,
              @JsonProperty() readonly phoneNumber?: string) {
    super(uid, firstname, lastname, profilePhoto);
    this.isGuest = !email;
  }
}

export class UserBuilder {

  @FormGen({name: "UID", type: "copyable"})
  readonly uid: string;
  @FormGen({name: "Firstname", type: "string"})
  firstname: string;
  @FormGen({name: "Lastname", type: "string"})
  lastname: string;
  @FormGen({name: "Profile photo", type: "profile_photo"})
  profilePhoto: string;
  @FormGen({name: "Email", type: "string"})
  email: string;
  created: number;
  @FormGen({name: "Phone number", type: "string"})
  phoneNumber: string;
  extras: any = {};

  constructor(user: User) {
    this.uid = user.uid;
    this.firstname = user.firstname;
    this.lastname = user.lastname;
    this.profilePhoto = user.profilePhoto;
    this.email = user.email;
    this.created = user.created;
    this.phoneNumber = user.phoneNumber;
    this.extras = user.extras;
  }

  build(): User {
    const user = new User(this.uid, this.firstname, this.lastname, this.profilePhoto, this.email, this.created, this.phoneNumber);
    Object.assign(user.extras, this.extras);
    return user;
  }
}

@JsonObject()
export class Member {

  user?: User;

  @JsonProperty()
  uid: string;

  @JsonProperty()
  memberId: string;

  @JsonProperty()
  username?: string;

  @JsonProperty()
  passwordUpdateRequired?: boolean;

  constructor(memberId: string, uid: string) {
    this.memberId = memberId;
    this.uid = uid;
  }

  isMe(): boolean {
    const auth = getAuth();
    if (!auth.currentUser) {
      return false;
    }
    return auth.currentUser.uid === this.uid;
  }

  static readonly USERS_REL_NAME = "users";
  static readonly USERNAME_REL_NAME = "username";

  usersRel() {
    return new Rel(Member.USERS_REL_NAME, this.uid, this.memberId, false, true);
  }

  usernameRel() {
    return new Rel(Member.USERNAME_REL_NAME, this.username, this.memberId, false, true);
  }
}

export class Members {

  private static instance: Members;

  static getInstance() {
    if (!this.instance) {
      this.instance = new Members();
    }
    return this.instance;
  }

  private readonly auth = getAuth();
  private readonly provisioningContext: ProvisioningContext;
  private members: Member[] = [];
  private botMembers: Member[] = [];

  private constructor() {
    const membersUseSharedProvisioningContext= Boolean(BaseApp.CONTEXT.getAppConfig()?.loginConfig?.provisioning?.membersUseSharedProvisioningContext);
    // We always use main provisioning for inhouse members.
    this.provisioningContext = (getProvisioningId() === SystemProvisioningIds.INHOUSE || membersUseSharedProvisioningContext) ? ProvisioningContext.MAIN : ProvisioningContext.DEFAULT;
  }

  // These are members that are pre-defined to exist in the app. For example, chatbot accounts etc.
  setBotMembers(members: Member[]) {
    this.botMembers = members;
  }

  async loadMembers(force?: boolean): Promise<void> {
    if (this.members && !force) {
      return;
    }
    const val = await this.provisioningContext.dbRef_getVal("members");
    const members: Member[] = [];
    if (val) {
      for (const key in val) {
        const member = await this.loadMember(key);
        if (member) {
          members.push(member);
        }
      }
    }
    const myUid = this.auth.currentUser.uid;
    this.members = members.filter(member => member.user.uid !== myUid);
  }

  async getOrLoadMembers(): Promise<Member[]> {
    if (!this.members) {
      await this.loadMembers();
    }
    return Promise.resolve(this.members || []);
  }

  getMembers(): Member[] {
    return this.members;
  }

  getMember(memberId: string): Member | undefined {
    return this.members?.find(value => value?.memberId === memberId);
  }

  getBotMember(memberId: string): Member | undefined {
    return this.botMembers?.find(value => value?.memberId === memberId);
  }

  async loadMember(memberId: string): Promise<Member | null> {
    const value = await this.provisioningContext.dbRef_getVal("members/" + memberId);
    if (value) {
      const member = JSON_OBJECT.deserializeObject(value, Member);
      member.user = await UserCache.getInstance().getUser(member.uid);
      return member;
    }
    return null;
  }

  async getOrLoadMember(memberId: string): Promise<Member | null> {
    let member = this.getMember(memberId);
    if (!member) {
      member = await this.loadMember(memberId);
      this.members.push(member);
    }
    return member;
  }

  async loadMemberByUid(uid: string): Promise<Member | null> {
    const value = await this.provisioningContext.dbRef_getVal("members" + Rel.oneToOnePathFor(Member.USERS_REL_NAME, uid));
    if (value) {
      return this.loadMember(value);
    }
    return Promise.resolve(null);
  }

  getMemberByUid(uid: string): Member | undefined {
    return this.members?.find(value => value.user?.uid === uid);
  }

  async getOrLoadMemberByUid(uid?: string, create?: boolean, createUsername?: boolean): Promise<Member | undefined> {
    if (!uid && create) {
      // Guest uid.
      uid = md5_uuid();
    }
    let member = this.getMemberByUid(uid);
    if (!member) {
      member = await this.loadMemberByUid(uid);
    }
    if (!member && create) {
      member = new Member(md5_uuid(), uid);
      if (createUsername) {
        const user = await UserCache.getInstance().getUser(uid);
        const usernamePath = StringUtil.toSlug(user.email?.substring(0, user.email.indexOf("@")) || "username");
        member.username = usernamePath + passkey({type: "numeric", length: 6});
      }
      await this.addMember(member);
    }
    return member;
  }

  async loadMemberByUsername(username: string): Promise<Member | null> {
    const value = await this.provisioningContext.dbRef_getVal("members" + Rel.oneToOnePathFor(Member.USERNAME_REL_NAME, username));
    if (value) {
      return this.loadMember(value);
    }
    return Promise.resolve(null);
  }

  getMemberByUsername(username: string): Member | undefined {
    return this.members?.find(value => value.username === username);
  }

  async getOrLoadMemberByUsername(username: string): Promise<Member | undefined> {
    let member = this.getMemberByUsername(username);
    if (!member) {
      member = await this.loadMemberByUsername(username);
    }
    return member;
  }

  async addMember(member: Member): Promise<void> {
    const object = JSON_OBJECT.serializeObject(member);
    this.members.push(member);
    // console.trace("adding member at: " + "members/" + member.memberId);
    return this.provisioningContext.dbRef_setVal("members/" + member.memberId, object, member.usersRel(), member.usernameRel());
  }

  async setMemberPart(memberId: string, partName: keyof Member, part: object): Promise<void> {
    const object = JSON_OBJECT.serializeObject(part);
    return this.provisioningContext.dbRef_setVal("members/" + memberId + "/" + partName, object);
  }

  async deleteMember(auth: Auth, member: Member): Promise<void> {
    const memberRef = this.provisioningContext.dbRef("members/" + member.memberId);
    return remove(memberRef);
  }
}
